<template>
  <template v-if="pagina">
    <StructuredData id="schema-webpage" :schema="webPageSchema" />
    <div class="overflow-hidden">
      <div
        class="landing-experiencia info-pagina"
        :class="{ 'with-index-block': hasIndexBlockDesignA }"
        :style="landingStyle">
        <template v-for="block in sortedBlocks" :key="block.id">
          <div
            :id="`block-${block.id}`"
            class="block-wrapper"
            :class="
              block.tipo !== BLOCK_TYPES.INDEX
                ? [getBlockSpacingClasses(block, pagina.style.spacing), 'block-reveal']
                : null
            "
            :style="getSectionWrapperStyle(block, pagina.style)">
            <div class="landing-content">
              <component
                :is="getBlockComponent(block.tipo, dispositivo, ENTITY_TYPES.PAGINA)"
                :block="block"
                :pagina="pagina" />
            </div>
          </div>
        </template>
      </div>
    </div>
  </template>
  <Home v-else-if="loaded && !route.params.uri" />
  <NotFound v-else-if="loaded" />
</template>

<script setup>
  import { computed, nextTick, onBeforeMount, onUnmounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import Home from '@/views/HomeView.vue';
  import NotFound from '@/components/NotFound.vue';
  import StructuredData from '@/components/utils/StructuredData.vue';
  import LandingPageApiService from '@/services/api/LandingPageApiService';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { usePageMeta } from '@/composables/usePageMeta';
  import {
    BLOCK_TYPES,
    DESIGN_A,
    DISPOSITIVO,
    ENTITY_TYPES,
    getBlockComponent,
    sortBlocksByOrder,
  } from '@/helpers/builderBlocksUtils';
  import {
    getBlockSpacingClasses,
    getSectionWrapperStyle,
    landingStyleVars,
    loadLandingFonts,
  } from '@/helpers/landingStyleUtils';
  import { setupLandingScrollReveal } from '@/helpers/scrollAnimationUtils';

  const route = useRoute();
  const loadingStore = useLoadingStore();

  const dispositivo = ref(getDispositivo());
  const stopReveal = ref(null);
  const loaded = ref(false);
  const pagina = ref(null);
  const blocks = ref([]);
  const sortedBlocks = computed(() => sortBlocksByOrder(blocks.value));
  const landingStyle = computed(() => landingStyleVars(pagina.value.style));
  const hasIndexBlockDesignA = computed(() =>
    blocks.value.some(block => block.tipo === BLOCK_TYPES.INDEX && block.design === DESIGN_A && block.visible)
  );
  const { webPageSchema } = usePageMeta({
    title: computed(() => (route.params.uri ? pagina.value?.titulo : undefined)),
  });

  function getDispositivo() {
    return window.innerWidth < 768 ? DISPOSITIVO.MOBILE : DISPOSITIVO.DESKTOP;
  }

  function handleResize() {
    dispositivo.value = getDispositivo();
  }

  async function loadLanding() {
    const { uri } = route.params;
    const { codigoPrevisualizacion } = route.query;
    stopReveal.value?.();
    try {
      loadingStore.show();
      pagina.value = uri
        ? await LandingPageApiService.getLandingPage(uri, codigoPrevisualizacion)
        : await LandingPageApiService.getHomepage();
      blocks.value = await LandingPageApiService.getLandingPageBuilderModules(pagina.value.uri, codigoPrevisualizacion);
      loadLandingFonts(pagina.value.style);
      await nextTick();
      stopReveal.value = await setupLandingScrollReveal();
    } catch (error) {
      pagina.value = null;
      console.error('Error al cargar la landing:', error);
    } finally {
      loaded.value = true;
      loadingStore.hide();
    }
  }

  watch(() => route.params.uri, loadLanding);

  onBeforeMount(() => {
    window.addEventListener('resize', handleResize);
    loadLanding();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    stopReveal.value?.();
  });
</script>

<style scoped lang="scss">
  .block-wrapper {
    scroll-margin-top: 180px;

    @media (max-width: 768px) {
      scroll-margin-top: 100px;
    }
  }

  .info-pagina {
    margin: 100px auto 0;
    position: relative;

    &.with-index-block {
      margin-top: 167px;

      @media (max-width: 768px) {
        margin-top: 152px;
      }
    }

    @media (max-width: 768px) {
      margin-top: 78px;
    }
  }
</style>
